import type { OfferItem, OfferResponse } from "services/offers";
import { PricingModelItem, PurchaseStudentType } from "services/pricingModel/model";
import { trackEvent } from "services/productAnalytics";
import { pushDataLayer } from "services/tagManager";

export type PurchaseAnalyticsEvent =
  | { name: "compare_plans_dialog_opened" }
  | {
      name: "purchase_form_shown";
      type: PurchaseStudentType | "all";
      pricingModelCode: string;
    }
  | {
      name: "pricing_model_selected";
      planName: string;
      code: string;
      type: PurchaseStudentType;
    }
  | ({
      name: "subscription_period_selected";
    } & SubscriptionPlanContext)
  | ({
      name: "subscription_plan_purchased";
    } & SubscriptionPlanContext)
  | ({
      name: "subscription_plan_purchase_error";
    } & SubscriptionPlanContext)
  | ({
      name: "lesson_package_selected";
    } & LessonPackageContext)
  | ({ name: "lesson_package_purchase_error" } & LessonPackageContext)
  | ({
      name: "lesson_package_purchased";
    } & LessonPackageContext)
  | { name: "subscription_payment_retry_clicked"; paymentStatus?: string }
  | { name: "subscription_payment_retry_succeeded" }
  | { name: "subscription_payment_retry_failed" };

type SubscriptionPlanContext = {
  period: string;
  price: number;
  currency: string;
  lengthDays: number;
};

type LessonPackageContext = {
  package_name: string;
  price: number;
  currency: string;
  lessonAmount: number;
};

export type CheckoutEventContext = {
  slug: string;
  item_id: string;
  price: number;
  currency: string;
};

export function trackPricingItemSelection(pricingItem: PricingModelItem) {
  if (pricingItem.purchase_type === "subscription") {
    trackEvent(
      "subscription_period_selected",
      getSubscriptionPlanTrackingContext(pricingItem)
    );
  } else {
    trackEvent("lesson_package_selected", getLessonPackageTrackingContext(pricingItem));
  }
}

export function trackPricingItemPurchase(pricingItem: PricingModelItem) {
  if (pricingItem.purchase_type === "subscription") {
    trackEvent(
      "subscription_plan_purchased",
      getSubscriptionPlanTrackingContext(pricingItem)
    );
  } else {
    trackEvent("lesson_package_purchased", getLessonPackageTrackingContext(pricingItem));
  }
}

export function trackPricingItemPurchaseError(pricingItem: PricingModelItem) {
  if (pricingItem.purchase_type === "subscription") {
    trackEvent(
      "subscription_plan_purchase_error",
      getSubscriptionPlanTrackingContext(pricingItem)
    );
  } else {
    trackEvent(
      "lesson_package_purchase_error",
      getLessonPackageTrackingContext(pricingItem)
    );
  }
}

export function trackPurchaseSubmitted(offer: OfferResponse, item: OfferItem) {
  trackEvent("purchase_submitted", getCheckoutEventContext(offer, item));
}

export function trackPurchaseSucceeded(offer: OfferResponse, item: OfferItem) {
  trackEvent("purchase_succeeded", getCheckoutEventContext(offer, item));
}

export function trackPurchaseFailed(
  offer: OfferResponse,
  item: OfferItem,
  reason: string
) {
  trackEvent("purchase_failed", { ...getCheckoutEventContext(offer, item), reason });
}

export const getSubscriptionPlanTrackingContext = (
  pricingItem: PricingModelItem
): SubscriptionPlanContext => ({
  period: pricingItem.name,
  price: pricingItem.price,
  currency: pricingItem.currency,
  lengthDays: pricingItem.length as number,
});

export const getLessonPackageTrackingContext = (
  pricingItem: PricingModelItem
): LessonPackageContext => ({
  package_name: pricingItem.name,
  price: pricingItem.price,
  currency: pricingItem.currency,
  lessonAmount: pricingItem.number as number,
});

function getCheckoutEventContext(
  offer: OfferResponse,
  item: OfferItem
): CheckoutEventContext {
  return {
    slug: offer.slug,
    item_id: item.id,
    price: item.price,
    currency: offer.currency,
  };
}

// The GA4 ecommerce events of the offer page, with their fields under `ecommerce`,
// which is where the container's GA4 tags read them from, and the setup events
// that follow a purchase.

export function pushOfferViewed(offer: OfferResponse, item: OfferItem) {
  pushDataLayer("view_item", { ecommerce: offerEcommerce(offer, item) });
}

export function pushCheckoutBegun(offer: OfferResponse, item: OfferItem) {
  pushDataLayer("begin_checkout", { ecommerce: offerEcommerce(offer, item) });
}

export function pushPurchase(offer: OfferResponse, item: OfferItem, intentId: string) {
  pushDataLayer("purchase", {
    ecommerce: { transaction_id: intentId, ...offerEcommerce(offer, item) },
  });
}

export function pushCheckoutSignUp() {
  pushDataLayer("sign_up", { method: "checkout" });
}

// About you is the one setup page pushed as a step: Goals pushes nothing, and
// Availability ends in `match_requested`.
export function pushSetupStep(step: "about") {
  pushDataLayer("setup_step", { step });
}

export function pushMatchRequested() {
  pushDataLayer("match_requested");
}

function offerEcommerce(offer: OfferResponse, item: OfferItem) {
  return {
    currency: offer.currency,
    value: item.price,
    items: [{ item_id: item.id, item_name: item.name, price: item.price }],
  };
}
